// READING a message that is still waiting on the clock (busy-my-web#442).
//
// Fatemeh, 2026-09-14: "it should open as an email and the scheduling message under it, that's all".
// So "View" on a scheduled send opens the message itself — /emails/scheduled/:id — and this module
// is everything that page KNOWS, kept out of the page so it can be asked without a DOM.
//
// Where the two halves come from, and why there are two:
//
//   • the SUMMARY the BFF lists (`GET /v1/email/scheduled` → { id, subject, sendAt, recipients } —
//     busy-main-bff#14's `ScheduledEmailSummary`). It is enough to say WHICH message and WHEN it
//     goes, and it survives a refresh, a new tab, and a link followed tomorrow;
//   • the COMPOSED copy the box kept when it scheduled — the payload it sent, which is the only
//     place the BODY exists client-side. The list route does not return one, so a message opened
//     in a later session is drawn from the summary alone, with its body simply absent rather than
//     invented. (If the BFF ever carries the body on the summary, `composed` stops mattering and
//     nothing here changes shape.)
//
// The two are merged in that order — the server's facts win on subject/recipients/time, the
// composed copy fills what the server does not send.
import { scheduleClock, scheduleDay } from '@/lib/scheduledSend'
import { scheduledRecipients } from '@/lib/emailPageEmpty'

/** A queued message with no subject reads as the mailbox's own "(No subject)", never as blank. */
export const NO_SUBJECT = '(No subject)'

/**
 * The line under the message (Fatemeh's "the scheduling message under it"): "Scheduled for Jul 22,
 * 12:30 PM". Written with the app's own two schedule formatters, so the moment reads the same here
 * as it does in the briefing fact and in the send notification.
 * An unreadable (or missing) moment says nothing at all rather than "Scheduled for Invalid Date".
 */
export function scheduledForLine (sendAt) {
  const day = scheduleDay(sendAt)
  const clock = scheduleClock(sendAt)
  if (!day || !clock) return ''
  return `Scheduled for ${day}, ${clock}`
}

/**
 * THE message the page was opened on, out of the list the folder reads. Case-insensitive on the id,
 * the rule the rest of the app applies to server ids: it is serialized in whatever case the server
 * chose and arrives in whatever case it was linked with.
 * Nothing asked for finds nothing — an id-less route has no message, not the first one.
 */
export function findScheduled (list, id) {
  const key = String(id ?? '').trim().toLowerCase()
  if (!key) return null
  return (Array.isArray(list) ? list : [])
    .find(entry => String(entry?.id ?? '').trim().toLowerCase() === key) || null
}

/** One address, as an email header writes it: the name when there is one, the address otherwise. */
export function addressText (person) {
  const name = String(person?.name || '').trim()
  const email = String(person?.email || person?.emailAddress || '').trim()
  if (name && email && name.toLowerCase() !== email.toLowerCase()) return `${name} <${email}>`
  return name || email
}

/** A whole header line — "Ada Lovelace <ada@lovelace.io>, grace@hopper.io". */
export function addressLine (people) {
  return (Array.isArray(people) ? people : [])
    .map(addressText)
    .filter(Boolean)
    .join(', ')
}

/**
 * What the box keeps when it schedules: the payload it sent, reduced to the parts a READER needs.
 * Kept as its own shape (rather than the whole payload) so what the page may show is stated once,
 * and so nothing the composer happens to carry — draft ids, attachment link plumbing — rides along
 * into the store.
 */
export function composedFromPayload (payload) {
  if (!payload) return null
  return {
    subject: String(payload.subject || ''),
    body: String(payload.body || ''),
    to: payload.toAddresses || [],
    cc: payload.ccAddresses || [],
    bcc: payload.bccAddresses || [],
    from: payload.fromAddress || null,
  }
}

/**
 * The message, as the page draws it. The server's summary is the truth about which message this is
 * and when it goes; the composed copy adds the body (and the from/cc/bcc lines the summary has no
 * field for). Either half may be missing: a record with no composed copy is a readable email
 * without its body, and a composed copy with no record is what the page has in the moment between
 * the schedule and the list coming back.
 */
export function scheduledMessageView ({ record = null, composed = null } = {}) {
  if (!record && !composed) return null
  const sendAt = record?.sendAt || record?.sendAtUtc || null
  const to = record?.recipients ? scheduledRecipients(record.recipients) : (composed?.to || [])
  return {
    id: String(record?.id ?? '') || '',
    subject: String(record?.subject || composed?.subject || '').trim() || NO_SUBJECT,
    from: addressLine(composed?.from ? [composed.from] : []),
    to: addressLine(to),
    cc: addressLine(composed?.cc || []),
    bcc: addressLine(composed?.bcc || []),
    // The composer's own HTML, as it was sent. Absent (a link followed in a later session) leaves
    // the body empty — the page says so in words rather than drawing an empty white box.
    bodyHtml: composed?.body || '',
    sendAt,
    scheduledLine: scheduledForLine(sendAt),
  }
}

export default scheduledMessageView
